import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import FlightSummaryCard from './FlightSummaryCard';
import FlightInformationCard from './FlightInformationCard';
import FlightActionsCard from './FlightActionsCard';
import { useSession } from '../SessionContext';
import InstructorLessonAssessment from '../InstructorLessonAssessment';
import { getTrainingAssessmentHistory } from '../trainingLessonAssessment';
import {
  buildAdsbMapUrl,
  fetchLiveAircraftPosition,
} from '../adsbService';
import {
  formatLocalDate,
  formatLocalTime,
  getBookingEndDate,
  getBookingStartDate,
  normalizeClockValue,
} from './bookingUtils';
import {
  getResolvedPostFlightStatus,
} from './activeFlightSelection';

export default function BookingDetailsModal({
  open,
  activeBooking,
  detailsError,
  isActiveBookingOwner,
  canEditTrainingAssignment,
  aircraftList,
  deletingBooking,
  getAircraftColour,
  openBookingModal,
  handleDeleteActiveBooking,
  closeDetailsModal,
  closeDetailsModalWithReturn,
}) {
  const navigate = useNavigate();
  const session = useSession();

  const currentUserId = String(
    session?.user?.id || ''
  ).trim();

  const bookingUserId = String(
    activeBooking?.user_id || ''
  ).trim();

  const canUsePersonalBookingActions =
    Boolean(currentUserId) &&
    Boolean(bookingUserId) &&
    currentUserId === bookingUserId;

  const [livePosition, setLivePosition] =
    useState(null);

  const [
    liveFlightChecked,
    setLiveFlightChecked,
  ] = useState(false);
  const [assessmentOpen, setAssessmentOpen] =
    useState(false);
  const [assessmentSaveStatus, setAssessmentSaveStatus] =
    useState('');
  const [assessmentBriefingData, setAssessmentBriefingData] =
    useState(activeBooking?.briefing_data || {});

  useEffect(() => {
    setAssessmentBriefingData(activeBooking?.briefing_data || {});
    setAssessmentOpen(false);
  }, [activeBooking?.id, activeBooking?.briefing_data]);

  useEffect(() => {
    setAssessmentSaveStatus('');
  }, [activeBooking?.id]);

  const aircraft = activeBooking
    ? aircraftList.find(
        (item) =>
          Number(item.id) ===
          Number(activeBooking.aircraft_id)
      )
    : null;

  useEffect(() => {
    setLivePosition(null);
    setLiveFlightChecked(false);

    if (
      !open ||
      !aircraft?.registration
    ) {
      return undefined;
    }

    let cancelled = false;

    async function checkLiveFlight() {
      try {
        const position =
          await fetchLiveAircraftPosition(
            aircraft.registration
          );

        if (!cancelled) {
          setLivePosition(position);
          setLiveFlightChecked(true);
        }
      } catch (_error) {
        if (!cancelled) {
          setLivePosition(null);
          setLiveFlightChecked(true);
        }
      }
    }

    checkLiveFlight();

    const interval = window.setInterval(
      checkLiveFlight,
      30000
    );

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [open, aircraft?.registration]);

  if (!activeBooking) return null;

  const colour = getAircraftColour(
    aircraft,
    aircraftList
  );

  const start =
    getBookingStartDate(activeBooking);

  const end =
    getBookingEndDate(activeBooking);

  const fallbackDate = activeBooking.date
    ? new Date(
        `${activeBooking.date}T12:00:00`
      )
    : null;

  const dateLabel = formatLongDate(
    start || fallbackDate
  );

  const endDateLabel = formatLongDate(end);

  const startTime = start
    ? formatLocalTime(start)
    : normalizeClockValue(
        activeBooking.time
      ) ||
      activeBooking.time ||
      'Unknown';

  const endTime = end
    ? formatLocalTime(end)
    : 'Unknown';

  const isMultiDay =
    Boolean(start && end) &&
    formatLocalDate(start) !==
      formatLocalDate(end);

  const routeFrom =
    activeBooking.departureAirfield ||
    activeBooking.departure_airfield ||
    'Not set';

  const routeTo =
    activeBooking.destinationAirfield ||
    activeBooking.destination_airfield ||
    'Not set';

  const notes = String(
    activeBooking.notes || ''
  ).trim();

  const pilotName =
    activeBooking.pilotName ||
    activeBooking.pilot_name ||
    activeBooking.pilotLabel ||
    'Pilot';

  const phone =
    activeBooking.pilotPhone ||
    activeBooking.pilot_phone ||
    activeBooking.phone ||
    '';

  const email =
    activeBooking.pilotEmail ||
    activeBooking.pilot_email ||
    activeBooking.email ||
    '';

  const flightStatus = getFlightStatus(
    activeBooking,
    start,
    end,
    isMultiDay
  );
  const identityCandidates = [
    session?.profile?.id,
    session?.user?.id,
  ].map((value) => String(value || '').trim()).filter(Boolean);
  const isAssignedInstructor = Boolean(
    activeBooking.instructor_user_id &&
      identityCandidates.includes(String(activeBooking.instructor_user_id))
  );
  const isTrainingStudent = Boolean(
    activeBooking.student_user_id &&
      identityCandidates.includes(String(activeBooking.student_user_id))
  );
  const role = String(session?.profile?.role || '').trim().toLowerCase();
  const canViewTrainingHistory =
    isTrainingStudent ||
    isAssignedInstructor ||
    ['admin', 'owner'].includes(role);
  const canAssessLesson =
    activeBooking.booking_type === 'training' &&
    canEditTrainingAssignment &&
    isAssignedInstructor &&
    flightStatus === '✓ COMPLETED';
  const assessmentHistory =
    getTrainingAssessmentHistory(assessmentBriefingData);

  const adsbMapUrl = buildAdsbMapUrl(
    aircraft?.adsb_icao_hex
  );

  function goTo(path) {
    closeDetailsModal();
    navigate(path);
  }

  function handleEditBooking() {
    closeDetailsModal();

    openBookingModal(
      '',
      activeBooking.date,
      activeBooking
    );
  }

  function goToPersonalAction(path) {
    if (!canUsePersonalBookingActions) {
      return;
    }

    goTo(path);
  }

  return (
    <Modal
      open={open}
      onClose={closeDetailsModalWithReturn}
    >
      <div style={styles.modal}>
        <FlightSummaryCard
          aircraft={aircraft}
          activeBooking={activeBooking}
          colour={colour}
          flightStatus={flightStatus}
          dateLabel={dateLabel}
          endDateLabel={endDateLabel}
          startTime={startTime}
          endTime={endTime}
          isMultiDay={isMultiDay}
        />

        {activeBooking.booking_type === 'training' && (
          <section style={styles.trainingSummary} aria-label="Training Flight">
            <div style={styles.trainingTitle}>Training Flight</div>
            <div style={styles.trainingRow}><strong>Student</strong><span style={styles.trainingValue}>{activeBooking.studentName || 'Student'}</span></div>
            <div style={styles.trainingRow}><strong>Instructor</strong><span style={styles.trainingValue}>{activeBooking.instructorName || 'Instructor'}</span></div>
            {activeBooking.courseLabel && (
              <div style={styles.trainingRow}><strong>Course</strong><span style={styles.trainingValue}>{activeBooking.courseLabel}</span></div>
            )}
          </section>
        )}

        {activeBooking.booking_type === 'training' &&
          canViewTrainingHistory &&
          assessmentHistory.length > 0 && (
            <section
              style={styles.assessmentHistory}
              aria-label="Lesson assessment history"
            >
              <div style={styles.trainingTitle}>Lesson assessment history</div>
              {assessmentHistory.map((assessment, index) => (
                <article
                  key={`${assessment.assessedAt || 'assessment'}-${index}`}
                  style={styles.assessmentEntry}
                >
                  <div style={styles.assessmentMeta}>
                    {assessment.assessedByName || 'Instructor'}
                    {assessment.assessedAt
                      ? ` · ${new Date(assessment.assessedAt).toLocaleString()}`
                      : ''}
                  </div>
                  {Array.isArray(assessment.exerciseAssessments) &&
                    assessment.exerciseAssessments.length > 0 && (
                    <ul style={styles.assessmentList}>
                      {assessment.exerciseAssessments.map((item) => (
                        <li key={`${item.itemId}-${item.outcome}`}>
                          {item.itemReference ? `${item.itemReference} — ` : ''}
                          {item.description}: {formatAssessmentOutcome(item.outcome)}
                        </li>
                      ))}
                    </ul>
                  )}
                  {assessment.overallOutcome && (
                    <div>
                      <strong>Overall lesson outcome:</strong>{' '}
                      {formatAssessmentOutcome(assessment.overallOutcome)}
                    </div>
                  )}
                  {assessment.lessonNotes && (
                    <div><strong>Lesson notes:</strong> {assessment.lessonNotes}</div>
                  )}
                  {assessment.nextLessonFocus && (
                    <div><strong>Next lesson focus:</strong> {assessment.nextLessonFocus}</div>
                  )}
                </article>
              ))}
            </section>
          )}

        {canAssessLesson && (
          <>
            {assessmentSaveStatus ? (
              <div style={styles.assessmentSaved} role="status">
                {assessmentSaveStatus}
              </div>
            ) : null}
            <button
              type="button"
              style={styles.assessmentButton}
              onClick={() => {
                setAssessmentSaveStatus('');
                setAssessmentOpen((openNow) => !openNow);
              }}
              aria-expanded={assessmentOpen}
            >
              {assessmentOpen ? 'Close lesson assessment' : 'Record lesson assessment'}
            </button>
            {assessmentOpen && (
              <InstructorLessonAssessment
                booking={{
                  ...activeBooking,
                  studentName: activeBooking.studentName || pilotName,
                  briefing_data: assessmentBriefingData,
                }}
                onCancel={() => setAssessmentOpen(false)}
                onSaved={(result) => {
                  setAssessmentBriefingData(result.briefingData);
                  setAssessmentSaveStatus('Lesson assessment saved.');
                  setAssessmentOpen(false);
                }}
              />
            )}
          </>
        )}

        <FlightInformationCard
          activeBooking={activeBooking}
          routeFrom={routeFrom}
          routeTo={routeTo}
          notes={notes}
        />

        {detailsError && (
          <div style={styles.detailsError} role="alert">
            {detailsError}
          </div>
        )}

        <FlightActionsCard
          isActiveBookingOwner={
            isActiveBookingOwner
          }
          canEditBooking={canEditTrainingAssignment}
          canUseBookingActions={
            canUsePersonalBookingActions
          }
          deletingBooking={deletingBooking}
          pilotName={pilotName}
          phone={phone}
          email={email}
          aircraftRegistration={
            aircraft?.registration ||
            activeBooking.aircraftRegistration ||
            activeBooking.registration ||
            'Aircraft'
          }
          bookingDateLabel={dateLabel}
          bookingStartTime={startTime}
          bookingEndTime={endTime}
          adsbMapUrl={adsbMapUrl}
          livePosition={livePosition}
          liveFlightChecked={
            liveFlightChecked
          }
          onEditBooking={
            handleEditBooking
          }
          onDeleteBooking={
            handleDeleteActiveBooking
          }
          onPreFlight={() =>
            goToPersonalAction(
              `/pre-flight?bookingId=${encodeURIComponent(
                String(activeBooking.id)
              )}`
            )
          }
        />

        <button
          type="button"
          style={styles.closeButton}
          onClick={
            closeDetailsModalWithReturn
          }
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

function getFlightStatus(
  booking,
  start,
  end,
  isMultiDay
) {
  const now = new Date();
  const postFlightStatus =
    getResolvedPostFlightStatus(booking);

  if (postFlightStatus === 'completed') {
    return '✓ COMPLETED';
  }

  if (postFlightStatus === 'did_not_fly') {
    return 'DID NOT TAKE PLACE';
  }

  if (
    start &&
    end &&
    now >= start &&
    now <= end
  ) {
    if (isMultiDay) {
      return `● AWAY UNTIL ${formatStatusDate(
        end
      )}`;
    }

    return '● CURRENT FLIGHT';
  }

  if (end && now > end) {
    return 'PAST BOOKING - CLOSEOUT UNCONFIRMED';
  }

  return 'BOOKED';
}

function formatStatusDate(date) {
  if (
    !date ||
    !Number.isFinite(date.getTime())
  ) {
    return 'RETURN';
  }

  return date
    .toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
    })
    .toUpperCase();
}

function formatLongDate(date) {
  if (
    !date ||
    !Number.isFinite(date.getTime())
  ) {
    return 'Unknown date';
  }

  return date.toLocaleDateString(
    undefined,
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }
  );
}

function formatAssessmentOutcome(outcome) {
  if (outcome === 'needs_practice') return 'Needs practice';
  if (outcome === 'satisfactory') return 'Satisfactory';
  return 'Not assessed';
}

const styles = {
  modal: {
    position: 'relative',
    color: '#d7ebff',
    textAlign: 'left',
  },

  closeButton: {
    width: '100%',
    marginTop: '0.8rem',
    border:
      '1px solid rgba(255,255,255,0.12)',
    background:
      'rgba(255,255,255,0.06)',
    color: '#d7ebff',
    borderRadius: 12,
    padding: '0.75rem',
    fontWeight: 850,
    cursor: 'pointer',
  },

  detailsError: {
    marginTop: '0.8rem',
    padding: '0.7rem 0.8rem',
    borderRadius: 10,
    background: 'rgba(125, 44, 44, 0.22)',
    border: '1px solid rgba(255, 141, 141, 0.34)',
    color: '#ffb4b4',
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },

  trainingSummary: {
    display: 'grid',
    gap: '0.4rem',
    marginTop: '0.8rem',
    padding: '0.75rem 0.85rem',
    border: '1px solid rgba(85, 199, 255, 0.28)',
    borderRadius: 8,
    background: 'rgba(85, 199, 255, 0.07)',
    fontSize: '0.85rem',
  },

  trainingTitle: {
    color: '#b6eaff',
    fontWeight: 900,
  },

  trainingRow: {
    display: 'grid',
    gridTemplateColumns: '6rem minmax(0, 1fr)',
    columnGap: '0.75rem',
    alignItems: 'start',
  },

  trainingValue: {
    minWidth: 0,
    overflowWrap: 'anywhere',
    lineHeight: 1.45,
  },

  assessmentHistory: {
    display: 'grid',
    gap: '0.6rem',
    marginTop: '0.8rem',
    padding: '0.75rem 0.85rem',
    border: '1px solid rgba(85, 199, 255, 0.28)',
    borderRadius: 8,
    background: 'rgba(85, 199, 255, 0.04)',
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },

  assessmentEntry: {
    display: 'grid',
    gap: '0.35rem',
    paddingTop: '0.55rem',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    overflowWrap: 'anywhere',
  },

  assessmentMeta: {
    color: '#a9c8df',
    fontSize: '0.76rem',
  },

  assessmentList: {
    display: 'grid',
    gap: '0.25rem',
    margin: 0,
    paddingLeft: '1.25rem',
  },

  assessmentButton: {
    width: '100%',
    marginTop: '0.7rem',
    border: 0,
    borderRadius: 8,
    padding: '0.7rem 0.85rem',
    background: '#1ecfff',
    color: '#181a2a',
    fontWeight: 850,
    cursor: 'pointer',
  },

  assessmentSaved: {
    marginTop: '0.7rem',
    padding: '0.65rem 0.8rem',
    border: '1px solid #2d806c',
    borderRadius: 7,
    background: '#12392f',
    color: '#c5f2df',
    fontSize: '0.84rem',
    fontWeight: 650,
  },
};